import React from 'react';
import { LeadFormWizard, LeadFormWizardOnComplete } from '../forms/LeadFormWizard';
import Box from '@mui/material/Box';

import { RebrandAlert } from '@lendica/components';

interface LeadCapturePageBProps {
    searchParams?: URLSearchParams;
    onComplete?: LeadFormWizardOnComplete;
    baseURL: string;
    requirePhone?: boolean;
    appType?: 'application' | 'dashboard';
    env?: string;
}

export const LeadCapturePageB = ({
    searchParams,
    onComplete,
    baseURL,
    requirePhone = true,
    appType = 'application',
    env = 'prod',
}: LeadCapturePageBProps) => {
    return (
        <Container>
            {/* Background Image */}
            <Box
                component={'picture'}
                sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 0,
                    img: {
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'top',
                    },
                }}
            >
                {/* ── WEBP (preferred) ─────────────────────────────── */}
                {/* desktop / large–landscape */}
                <source
                    type="image/webp"
                    media="(min-width:1280px)"
                    srcSet="https://static.golendica.com/assets/img/dash-1920.webp 1x,
                            https://static.golendica.com/assets/img/dash-3840.webp 2x"
                />

                {/* tablet / small–landscape */}
                <source
                    type="image/webp"
                    media="(min-width:640px)"
                    srcSet="https://static.golendica.com/assets/img/dash-1280.webp 1x,
                            https://static.golendica.com/assets/img/dash-2560.webp 2x"
                />

                {/* phone / default */}
                <source
                    type="image/webp"
                    srcSet="https://static.golendica.com/assets/img/dash-640.webp 1x,
                            https://static.golendica.com/assets/img/dash-1280.webp 2x"
                />

                {/* PNG FALLBACK (legacy browsers) ──────────────── */}
                <source
                    type="image/png"
                    media="(min-width:1280px)"
                    srcSet="https://static.golendica.com/assets/img/dash-1920.png 1x,
                            https://static.golendica.com/assets/img/dash-3840.png 2x"
                />

                <source
                    type="image/png"
                    media="(min-width:640px)"
                    srcSet="https://static.golendica.com/assets/img/dash-1280.png 1x,
                        https://static.golendica.com/assets/img/dash-2560.png 2x"
                />

                {/* phone fallback if everything above is skipped */}
                <img
                    src="https://static.golendica.com/assets/img/dash-640.png"
                    srcSet="https://static.golendica.com/assets/img/dash-1280.png 2x"
                    alt="Dashboard preview"
                    loading="lazy"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top',
                    }}
                />
            </Box>

            {/* Progressive Backdrop Blur */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 5,
                    background: 'rgba(18, 20, 22, 0.25)',
                    backdropFilter: `
                        blur(2.5px)
                    `,
                    WebkitBackdropFilter: `
                        blur(2.5px)
                    `,
                }}
            />

            {/* Form */}
            <Box
                sx={theme => ({
                    width: { xs: '100%', md: 'fit-content' },
                    maxWidth: { xs: '100%', md: 'none' },
                    height: 'max-content',
                    minHeight: { xs: '100vh', md: 674 }, // Full height on mobile, consistent card height on desktop
                    p: 0,
                    m: 0,
                    borderRadius: { xs: 0, md: 2 }, // No border radius on mobile, rounded on desktop
                    background: theme.palette.background.paper,
                    boxShadow: { xs: 'none', md: theme.shadows[6] }, // No shadow on mobile, shadow on desktop
                    zIndex: 10,
                    // Smooth transitions for height changes
                    transition: theme.transitions.create(['height'], {
                        duration: theme.transitions.duration.standard,
                    }),
                })}
            >
                <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, right: 0 }}>
                    <RebrandAlert sx={{ width: '100%', borderRadius: 0 }} />
                </Box>
                <LeadFormWizard
                    searchParams={searchParams}
                    onComplete={onComplete}
                    baseURL={baseURL}
                    requirePhone={requirePhone}
                    appType={appType}
                    env={env}
                />
            </Box>
        </Container>
    );
};

const Container = ({ children }: { children: React.ReactNode }) => {
    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                minHeight: '100vh',
                p: { xs: 0, md: 3 }, // Small padding on mobile, more on desktop
                background: theme => theme.palette.background.paper,
            }}
        >
            {children}
        </Box>
    );
};
